import { Observable } from './observable.js';

/**
 * Interface for an object that modifies the intersection area of an element.
 */
export interface IntersectionModifier {
    /** The number of pixels to add to the top of the intersection area. */
    top: number;
    /** The number of pixels to add to the left of the intersection area. */
    left: number;
    /** The number of pixels to add to the bottom of the intersection area. */
    bottom: number;
    /** The number of pixels to add to the right of the intersection area. */
    right: number;
}

/**
 * Options for creating an ElementIntersectionObservable.
 */
export interface ElementIntersectionObservableInit {
    /** The first Element to observe. */
    element1: Element | null;
    /** The second Element to observe. */
    element2: Element | null;
    /** Optional IntersectionModifier object to adjust the element1 intersection area. */
    modifier1?: IntersectionModifier;
    /** Optional IntersectionModifier object to adjust the element2 intersection area. */
    modifier2?: IntersectionModifier;
    /** Optional interval time in milliseconds to save resources */
    intervalTime?: number;
}

const DEFAULT_MODIFIER: IntersectionModifier = { top: 0, left: 0, bottom: 0, right: 0 };

/**
 * Checks if two DOMRect objects intersect.
 *
 * @param rect1 The first DOMRect object.
 * @param rect2 The second DOMRect object.
 * @param modifier1 Optional IntersectionModifier object to adjust the rect1 intersection area.
 * @param modifier2 Optional IntersectionModifier object to adjust the rect2 intersection area.
 *
 * @returns true if the two rectangles intersect, false otherwise.
 */
const isIntersecting = (rect1: DOMRect, rect2: DOMRect, modifier1: IntersectionModifier = DEFAULT_MODIFIER, modifier2: IntersectionModifier = DEFAULT_MODIFIER): boolean => (
    rect1.left + modifier1.left < rect2.right + modifier2.right &&
    rect1.right + modifier1.right > rect2.left + modifier2.left &&
    rect1.top + modifier1.top < rect2.bottom + modifier2.bottom &&
    rect1.bottom + modifier1.bottom > rect2.top + modifier2.top
);

/**
 * Creates an Observable that emits a boolean value whenever two elements intersect.
 *
 * @param options Options for creating the ElementIntersectionObservable.
 *
 * @returns An Observable that emits a boolean value indicating if the two elements intersect.
 */
export const createElementIntersectionObservable = ({ element1, element2, modifier1, modifier2, intervalTime = 100 }: ElementIntersectionObservableInit) => new Observable<boolean>(subscriber => {
    let intersecting: boolean | undefined = undefined;

    const checkIntersection = () => requestAnimationFrame(() => {
        if (!subscriber.isActive) return;

        const rect1 = element1?.getBoundingClientRect();
        const rect2 = element2?.getBoundingClientRect();

        const intersectingNow = rect1 && rect2 ? isIntersecting(rect1, rect2, modifier1, modifier2) : false;

        if (intersectingNow !== intersecting) {
            intersecting = intersectingNow;
            subscriber.next(intersecting);
        }
    });

    const intervalId = window.setInterval(checkIntersection, intervalTime);
    subscriber.addTeardown(() => {
        window.clearInterval(intervalId);
    });

    checkIntersection();
});
